import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { Help, ModalShell, btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { isApprovalRequired } from "../../features/gamification/service";
import {
  REFERRAL_PAIRS,
  referralService,
  type ReferralAction,
  type ReferralAmounts,
  type ReferralPair,
  type ReferralRow,
  type ReferralSettings,
} from "../../features/gamification/referral";
import { LoadError, Pager, PersonCell, StatusBadge, dash, tdCls, thCls, usePaged } from "./GamificationPanels";

const PAIR_LABEL: Record<ReferralPair, string> = {
  seller_seller: "Seller refers a seller",
  seller_buyer: "Seller refers a buyer",
  buyer_seller: "Buyer refers a seller",
  buyer_buyer: "Buyer refers a buyer",
};

const FLAG_LABEL: Record<string, string> = {
  same_email: "same email",
  same_phone: "same phone",
  same_device: "same device",
  same_ip: "same network",
  same_network_and_device: "Same network AND same device",
  ip_cluster: "many from one network",
  device_cluster: "many from one device",
  no_device: "no device id sent",
};

const STATUS_FILTERS = ["", "attached", "flagged", "blocked", "rewarded", "skipped", "rejected", "reversed"];

const REASON_LABEL: Record<string, string> = {
  waiting_for_onboarding: "waiting for the new user to finish onboarding",
  waiting_for_referrer_onboarding: "waiting for the referrer to finish onboarding",
  monthly_limit: "referrer reached the monthly limit",
  monthly_budget: "monthly budget used up",
  user_cap: "30-day limit reached",
  referrer_not_allowed: "referrer is disabled",
  no_reward_configured: "no reward set for this combination",
};

// ── Settings ─────────────────────────────────────────────────────────────────
function SettingsCard({ toast }: { toast: ReturnType<typeof useAdminToast> }) {
  const [settings, setSettings] = useState<ReferralSettings | null>(null);
  const [error, setError] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [limit, setLimit] = useState("10");
  const [threshold, setThreshold] = useState("3");
  const [blockSame, setBlockSame] = useState(true);
  const [amounts, setAmounts] = useState<Record<ReferralPair, { referrer: string; referred: string }> | null>(null);
  const [saving, setSaving] = useState(false);

  const apply = (s: ReferralSettings) => {
    setSettings(s);
    setEnabled(s.referral_enabled);
    setLimit(String(s.monthly_limit));
    setThreshold(String(s.cluster_flag_threshold));
    setBlockSame(s.block_same_network_and_device !== false);
    setAmounts(Object.fromEntries(REFERRAL_PAIRS.map((p) => [p, { referrer: String(s.amounts[p].referrer), referred: String(s.amounts[p].referred) }])) as Record<ReferralPair, { referrer: string; referred: string }>);
  };

  const load = useCallback(async () => {
    try {
      apply(await referralService.getSettings());
      setError("");
    } catch (e) {
      setError(errMsg(e, "Failed to load the referral settings"));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (error) return <section className={cardCls}><LoadError message={error} onRetry={() => void load()} /></section>;
  if (!settings || !amounts) return <section className={cardCls}><Spinner /></section>;

  const wholeNumber = (v: string) => /^\d{1,6}$/.test(v.trim());
  const valid = wholeNumber(limit) && wholeNumber(threshold) && Number(threshold) >= 2
    && REFERRAL_PAIRS.every((p) => wholeNumber(amounts[p].referrer) && wholeNumber(amounts[p].referred));

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const body = {
        referral_enabled: enabled,
        monthly_limit: Number(limit),
        cluster_flag_threshold: Number(threshold),
        block_same_network_and_device: blockSame,
        amounts: Object.fromEntries(REFERRAL_PAIRS.map((p) => [p, { referrer: Number(amounts[p].referrer), referred: Number(amounts[p].referred) } as ReferralAmounts])),
      };
      const res = await referralService.updateSettings(body);
      if (isApprovalRequired(res)) {
        toast.showSuccess("Sent for approval. A different admin has to approve it in the Approvals tab; nothing changed yet.");
      } else {
        apply(res);
        toast.showSuccess("Referral settings saved");
      }
    } catch (e) {
      toast.showError(errMsg(e, "Failed to save the referral settings"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Referral settings</h2>
      <p className="text-gray-500 text-xs mb-4">
        A new user can enter a friend's code at sign-up. When the new user finishes onboarding, both get free credits (they expire like other free credits).
      </p>
      <p className="text-gray-400 text-xs mb-3">Referral works on its own: it does not need the main rewards switch. The 30-day credit cap, the monthly budget and the monthly referral limit still apply.</p>
      <label className="flex items-center gap-2 text-sm text-gray-200 mb-1">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Referral rewards are ON
      </label>
      <Help>Ships OFF. Switch off at any time to stop new codes and payouts at once; nothing already paid is changed.</Help>

      <div className="overflow-x-auto mt-4">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-xs text-gray-500">
              <th className={thCls}>Who refers whom</th>
              <th className={thCls}>Credits to the referrer</th>
              <th className="pb-2 font-medium">Credits to the new user</th>
            </tr>
          </thead>
          <tbody>
            {REFERRAL_PAIRS.map((p) => (
              <tr key={p} className="border-t border-gray-800 text-gray-300">
                <td className={tdCls}>{PAIR_LABEL[p]}</td>
                {(["referrer", "referred"] as const).map((side) => (
                  <td key={side} className={tdCls}>
                    <input
                      aria-label={`${PAIR_LABEL[p]}: credits to the ${side === "referrer" ? "referrer" : "new user"}`}
                      className={`${inputCls} max-w-[8rem]`}
                      inputMode="numeric"
                      value={amounts[p][side]}
                      onChange={(e) => setAmounts({ ...amounts, [p]: { ...amounts[p], [side]: e.target.value } })}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-200 mt-4 mb-1">
        <input type="checkbox" checked={blockSame} onChange={(e) => setBlockSame(e.target.checked)} />
        Block when the same network AND the same device
      </label>
      <Help>Blocked only when the new account uses the same Wi-Fi/network and the same phone or browser as the referrer. Turn off only for testing. Same email, same phone number or your own code are always blocked.</Help>

      <div className="grid gap-4 sm:grid-cols-2 mt-4 max-w-xl">
        <div>
          <label className={labelCls} htmlFor="ref-limit">Rewarded referrals per person per month</label>
          <input id="ref-limit" className={inputCls} inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value)} />
          <Help>Default 10. After that a referral is accepted but not paid. 0 pays nobody.</Help>
        </div>
        <div>
          <label className={labelCls} htmlFor="ref-threshold">Hold for your review when this many people signed up from the same network or device (30 days)</label>
          <input id="ref-threshold" className={inputCls} inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          <Help>Default 3. Example: if 3 or more accounts joined with referral codes from the same Wi-Fi or the same phone within 30 days, those referrals are put on hold (status "flagged") and nobody is paid until you approve each one in the list below. This catches one person making many fake accounts. Reject a held referral to pay nothing.</Help>
        </div>
      </div>
      <p className="text-gray-500 text-xs mt-3">Every payout also counts toward the per-user 30-day limit and the monthly budget (Settings tab). The referrer has to be onboarded and not disabled. Same email or phone as the referrer is always blocked; same network and same device together is blocked unless you turn that off above.</p>
      <div className="mt-4">
        <button className={btnPrimary} disabled={!valid || saving} onClick={() => void save()}>{saving ? "Saving…" : "Save referral settings"}</button>
        {!valid && <span className="text-red-400 text-xs ml-3">Use whole numbers (the hold threshold is at least 2).</span>}
      </div>
    </section>
  );
}

// ── List ─────────────────────────────────────────────────────────────────────
const ACTIONS_FOR: Record<string, ReferralAction[]> = {
  attached: ["flag", "reject", "approve"],
  flagged: ["approve", "reject"],
  blocked: ["approve"],
  rewarded: ["reverse"],
};
const ACTION_TEXT: Record<ReferralAction, { button: string; title: string; hint: string }> = {
  flag: { button: "Flag", title: "Hold this referral for review", hint: "No reward is paid until you approve it." },
  approve: { button: "Approve", title: "Approve and pay this referral", hint: "Pays both sides now if the new user finished onboarding, otherwise when they do. For a blocked row this only works when it was blocked for the same network/device; the server refuses same email or phone." },
  reject: { button: "Reject", title: "Reject this referral", hint: "Nothing is paid." },
  reverse: { button: "Reverse", title: "Reverse this reward", hint: "Takes the credits back from both sides (an audited take-back; allowed within the take-back window)." },
};

function ReasonModal({ row, action, onClose, onDone }: { row: ReferralRow; action: ReferralAction; onClose: () => void; onDone: (message: string) => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const text = ACTION_TEXT[action];
  const submit = async () => {
    setBusy(true);
    try {
      const res = await referralService.act(row.id, action, reason.trim());
      onDone(isApprovalRequired(res) ? "Sent for approval (Approvals tab). Nothing changed yet." : `${text.button}: done`);
    } catch (e) {
      setError(errMsg(e, "That did not work"));
      setBusy(false);
    }
  };
  return (
    <ModalShell title={text.title} onClose={onClose}>
      <p className="text-gray-400 text-sm mb-1">{row.referrer_name || row.referrer_email || row.referrer_id} referred {row.referred_name || row.referred_email || row.referred_id}.</p>
      <Help>{text.hint}</Help>
      <label className={`${labelCls} mt-3`} htmlFor="ref-reason">Reason (required, kept in the audit log)</label>
      <textarea id="ref-reason" className={inputCls} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      {error && <p className="text-red-400 text-sm mt-2" role="alert">{error}</p>}
      <div className="flex gap-2 justify-end mt-4">
        <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !reason.trim()} onClick={() => void submit()}>{busy ? "Working…" : text.button}</button>
      </div>
    </ModalShell>
  );
}

function ListCard({ toast }: { toast: ReturnType<typeof useAdminToast> }) {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState({ status: "", q: "" });
  const [acting, setActing] = useState<{ row: ReferralRow; action: ReferralAction } | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const fetcher = useCallback(async (page: number) => {
    const res = await referralService.list(page, applied.status, applied.q);
    setCounts(res.status_counts);
    return res;
  }, [applied]);
  const { data, loading, error, load } = usePaged(fetcher, "Failed to load the referrals");
  useEffect(() => { void load(1); }, [load]);

  const exportCsv = async () => {
    try {
      await referralService.exportCsv(applied.status, applied.q);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to export the referrals"));
    }
  };

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Referrals</h2>
      <p className="text-gray-500 text-xs mb-4">Who referred whom, what was paid and why something was held or blocked.</p>
      <form className="flex flex-wrap gap-2 items-end mb-3" onSubmit={(e) => { e.preventDefault(); setApplied({ status, q: q.trim() }); }}>
        <div>
          <label className={labelCls} htmlFor="ref-status">Status</label>
          <select id="ref-status" className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All"}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="ref-q">Name, email or phone</label>
          <input id="ref-q" className={`${inputCls} max-w-xs`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="at least 3 characters" />
        </div>
        <button type="submit" className={btnPrimary} disabled={loading}>Filter</button>
        <button type="button" className={btnGhost} onClick={() => void exportCsv()}>Export CSV</button>
      </form>
      {data && (
        <p className="text-xs text-gray-500 mb-2">
          {Object.entries(counts).map(([s, n]) => `${s.replace(/_/g, " ")}: ${n}`).join(" · ") || "No referrals yet"}
        </p>
      )}
      {error && <LoadError message={error} onRetry={() => void load(1)} />}
      {loading && !data && <Spinner />}
      {data && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className={thCls}>Date</th>
                  <th className={thCls}>Referrer</th>
                  <th className={thCls}>New user</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Credits (referrer / new)</th>
                  <th className="pb-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr><td colSpan={6} className="py-4 text-gray-500">Nothing here.</td></tr>
                )}
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t border-gray-800 text-gray-300 align-top">
                    <td className={tdCls}>{r.created_at ? formatISTDateTime(r.created_at) : "—"}</td>
                    <td className={tdCls}><PersonCell id={r.referrer_id} name={r.referrer_name} email={r.referrer_email} phone={r.referrer_phone} /><span className="text-xs text-gray-500">{dash(r.referrer_role)}</span></td>
                    <td className={tdCls}><PersonCell id={r.referred_id} name={r.referred_name} email={r.referred_email} phone={r.referred_phone} /><span className="text-xs text-gray-500">{dash(r.referred_role)}</span></td>
                    <td className={tdCls}>
                      <StatusBadge status={r.status} />
                      {r.fraud_flags.length > 0 && <span className="block text-xs text-amber-300 mt-1">{r.fraud_flags.map((f) => FLAG_LABEL[f] ?? f).join(", ")}</span>}
                      {r.status_note && <span className="block text-xs text-sky-300 mt-1">{r.status_note}</span>}
                      {r.reason && <span className="block text-xs text-gray-500 mt-1">{REASON_LABEL[r.reason] ?? r.reason.replace(/_/g, " ")}</span>}
                      {r.decision_note && <span className="block text-xs text-gray-500 mt-1">Admin note: {r.decision_note}</span>}
                    </td>
                    <td className={tdCls}>{r.status === "rewarded" || r.status === "reversed" ? `${r.referrer_credits} / ${r.referred_credits}` : "—"}</td>
                    <td className={tdCls}>
                      <div className="flex flex-wrap gap-1">
                        {(ACTIONS_FOR[r.status] ?? []).map((a) => (
                          <button key={a} className={btnGhost} onClick={() => setActing({ row: r, action: a })}>{ACTION_TEXT[a].button}</button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={data} onPage={(p) => void load(p)} busy={loading} />
        </>
      )}
      {acting && (
        <ReasonModal
          row={acting.row}
          action={acting.action}
          onClose={() => setActing(null)}
          onDone={(message) => { setActing(null); toast.showSuccess(message); void load(data?.page ?? 1); }}
        />
      )}
    </section>
  );
}

export default function ReferralPanel() {
  const toast = useAdminToast();
  return (
    <div className="space-y-6">
      <SettingsCard toast={toast} />
      <ListCard toast={toast} />
    </div>
  );
}
